import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiGetAll, apiPost } from 'api/service';
import { cancelScan } from 'api/features';
import {
  alpha, Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Drawer,
  IconButton, LinearProgress, Pagination, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { providerLabel } from './cloudScanLogic';
import { GRADE_COLOR, GRADE_TEXT } from './mobileScanLogic';
import { buildPosture, byCategory, overall } from './postureLogic';
import { GREEN, MONO, SEV, SEV_COLOR, ago, th, took } from './scanUi';

const CLOUD_TYPES = 'cloud_aws_audit,cloud_multi_audit';
const PAGE_SIZE = 15;
const scoreColor = (s) => (s >= 90 ? '#22c55e' : s >= 80 ? '#84cc16' : s >= 70 ? '#eab308' : s >= 60 ? '#f97316' : '#ef4444');

function Ring({ score, grade, size = 92 }) {
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <CircularProgress variant="determinate" value={100} size={size} thickness={5} sx={{ position: 'absolute', color: 'action.hover' }} />
      <CircularProgress variant="determinate" value={score} size={size} thickness={5} sx={{ position: 'absolute', color: GRADE_COLOR[grade] }} />
      <Typography sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size / 3, fontWeight: 800, fontFamily: MONO, color: GRADE_COLOR[grade] }}>{grade}</Typography>
    </Box>
  );
}

export default function CloudPosturePage() {
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });
  const [sevFilter, setSevFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [openKey, setOpenKey] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const toast = (severity, message) => setSnack({ open: true, severity, message });

  const load = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([apiGet('/api/cloud-scans/'), apiGetAll(`/api/findings/?scan_type=${CLOUD_TYPES}`)]);
      setScans(s?.results || s || []);
      setFindings(f || []);
    } catch (e) { setError(e?.message || 'Failed to load cloud audits.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const accounts = useMemo(() => buildPosture(scans, findings), [scans, findings]);
  const live = accounts.filter((a) => a.live);
  useEffect(() => {
    if (!live.length) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [live.length, load]);

  const total = overall(accounts);
  const sums = accounts.reduce((t, a) => ({
    critical: t.critical + (a.risk?.counts.critical || 0), high: t.high + (a.risk?.counts.high || 0), medium: t.medium + (a.risk?.counts.medium || 0)
  }), { critical: 0, high: 0, medium: 0 });

  // Problems from every account's newest audit, worst first.
  const problems = useMemo(() => accounts.flatMap((a) => a.findings.map((f) => ({ ...f, account: a.name, key: a.key })))
    .sort((x, y) => SEV.indexOf(x.severity) - SEV.indexOf(y.severity)), [accounts]);
  const shown = problems.filter((p) => sevFilter === 'all' || p.severity === sevFilter);
  const paged = shown.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const open = accounts.find((a) => a.key === openKey) || null;

  const repeat = (a) => apiPost('/api/cloud-scans/', { user_target: a.latest.user_target || a.userTarget, scan_type: a.latest.scan_type, options: a.latest.options || {} });
  const run = async (fn, okMessage) => {
    setBusy(true); setError('');
    try { await fn(); toast('success', okMessage); await load(); }
    catch (e) { setError(e?.message || 'That did not work.'); }
    finally { setBusy(false); }
  };
  const auditAll = () => run(async () => {
    const todo = accounts.filter((a) => !a.live);
    if (!todo.length) throw new Error('Every account is already being audited.');
    await Promise.all(todo.map(repeat));
  }, 'Audits started.');

  if (!loading && accounts.length === 0) {
    return (
      <Box className="fuzzscan-page" sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>Cloud posture</Typography>
        <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => navigate('/cloud-scans')}>Open Cloud scans</Button>}>
          No cloud accounts have been audited yet. Run an audit on the Cloud scans page and each account appears here with its score.
        </Alert>
      </Box>
    );
  }

  const card = { border: 1, borderColor: 'divider', borderRadius: 4, bgcolor: 'background.paper' };
  const trend = (a) => (a.risk && a.prevRisk ? a.risk.score - a.prevRisk.score : null);

  return (
    <Box className="fuzzscan-page" sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Cloud posture</Typography>
          <Typography variant="body2" color="text.secondary">How safe each of your cloud accounts is, from its latest audit</Typography>
        </Box>
        <Button variant="contained" disabled={busy} onClick={auditAll} sx={{ minHeight: 44, px: 3, bgcolor: GREEN, color: '#06220F', fontWeight: 700, '&:hover': { bgcolor: '#16a34a' } }}>Audit all accounts</Button>
      </Stack>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2.5} sx={{ mb: 3 }}>
        <Box sx={{ ...card, p: 3, display: 'flex', alignItems: 'center', gap: 2.5, flex: '1 1 300px' }}>
          {total ? <Ring score={total.score} grade={total.grade} /> : <Box sx={{ width: 92 }} />}
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>Overall posture</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{total ? GRADE_TEXT[total.grade] : 'No audit finished yet'}</Typography>
            <Typography variant="body2" color="text.secondary">{total ? `Average score ${total.score} across ${total.count} account${total.count === 1 ? '' : 's'}` : 'Scores appear after the first audit ends.'}</Typography>
          </Box>
        </Box>
        <Box sx={{ flex: '2 1 520px', display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
          {[['Accounts', accounts.length, '#64748b'], ['Critical', sums.critical, SEV_COLOR.critical], ['High', sums.high, SEV_COLOR.high], ['Medium', sums.medium, SEV_COLOR.medium]].map(([label, value, color]) => (
            <Box key={label} sx={{ ...card, borderTop: `3px solid ${color}`, borderRadius: 3.5, p: 2 }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography sx={{ fontSize: 30, fontWeight: 800, fontFamily: MONO, color: label === 'Accounts' ? 'inherit' : color, mt: 0.5 }}>{value}</Typography>
            </Box>
          ))}
        </Box>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 2.5, mb: 3, alignItems: 'start' }}>
        {accounts.map((a) => (
          <Box key={a.key} sx={{ ...card, p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.6, borderColor: a.risk?.counts.critical ? alpha('#ef4444', 0.45) : a.live ? GREEN : 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2.5, bgcolor: alpha('#fb923c', 0.16), color: '#fdba74', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, flexShrink: 0 }}>{providerLabel(a.provider).slice(0, 3)}</Box>
                <Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ fontWeight: 700, fontSize: 16 }}>{a.name}</Typography><Typography noWrap variant="caption" color="text.secondary">{a.meta}</Typography></Box>
              </Stack>
              {a.risk ? (
                <Box sx={{ textAlign: 'right' }}><Typography sx={{ fontSize: 28, fontWeight: 800, fontFamily: MONO, color: scoreColor(a.risk.score), lineHeight: 1 }}>{a.risk.score}</Typography><Typography variant="caption" color="text.secondary">of 100</Typography></Box>
              ) : <Chip size="small" label="NO AUDIT YET" sx={{ fontWeight: 700, fontSize: 11 }} />}
            </Stack>
            {a.risk && <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover' }}><Box sx={{ height: '100%', width: `${a.risk.score}%`, borderRadius: 3, bgcolor: scoreColor(a.risk.score) }} /></Box>}
            {a.live && <Box><LinearProgress variant="determinate" value={Number(a.live.progress ?? 0)} sx={{ height: 5, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: GREEN } }} /><Typography variant="caption" color="text.secondary">{a.live.progress_message || 'Auditing…'}</Typography></Box>}
            <Stack direction="row" gap={2.5}>
              {SEV.slice(0, 4).map((k) => (
                <Box key={k}><Typography sx={{ fontSize: 20, fontWeight: 800, fontFamily: MONO, color: SEV_COLOR[k] }}>{a.risk ? a.risk.counts[k] || 0 : '—'}</Typography><Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{k}</Typography></Box>
              ))}
            </Stack>
            <Stack direction="row" alignItems="center" gap={1} sx={{ borderTop: 1, borderColor: 'divider', pt: 1.6 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{a.done ? `Audited ${ago(a.done.finished_at || a.done.created_at)}` : `${a.runs} run${a.runs === 1 ? '' : 's'}, none finished`}</Typography>
              <Button size="small" variant="outlined" disabled={!a.done} onClick={() => setOpenKey(a.key)} sx={{ borderColor: 'divider', color: 'text.primary', fontWeight: 600 }}>Details</Button>
              {a.live
                ? <Button size="small" variant="outlined" color="error" onClick={() => setConfirm(a.live)}>Stop</Button>
                : <Button size="small" variant="contained" disabled={busy} onClick={() => run(() => repeat(a), 'Audit started.')} sx={{ bgcolor: GREEN, color: '#06220F', fontWeight: 700, '&:hover': { bgcolor: '#16a34a' } }}>Audit again</Button>}
            </Stack>
          </Box>
        ))}
      </Box>

      <Box sx={{ ...card, overflow: 'hidden' }}>
        <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={1.5} sx={{ p: 2.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Top misconfigurations</Typography>
          <Stack direction="row" gap={0.8}>
            {[['all', `All ${problems.length}`], ['critical', `Critical ${problems.filter((p) => p.severity === 'critical').length}`], ['high', `High ${problems.filter((p) => p.severity === 'high').length}`]].map(([id, label]) => (
              <Chip key={id} clickable label={label} onClick={() => { setSevFilter(id); setPage(1); }}
                sx={{ fontWeight: 600, border: 1, borderColor: sevFilter === id ? alpha(GREEN, 0.5) : 'divider', bgcolor: sevFilter === id ? alpha(GREEN, 0.16) : 'transparent', color: sevFilter === id ? GREEN : 'text.secondary' }} />
            ))}
          </Stack>
        </Stack>
        <TableContainer>
          <Table sx={{ minWidth: 860 }}>
            <TableHead><TableRow><TableCell sx={{ ...th, pl: 2.5 }}>Problem</TableCell><TableCell sx={th}>Severity</TableCell><TableCell sx={th}>Resource</TableCell><TableCell sx={th}>Account</TableCell><TableCell sx={th}>Found</TableCell></TableRow></TableHead>
            <TableBody>
              {paged.length === 0 && <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>{problems.length ? 'Nothing at this level.' : 'The latest audits found no problems.'}</Typography></TableCell></TableRow>}
              {paged.map((p) => (
                <TableRow key={`${p.key}-${p.id}`} hover onClick={() => setOpenKey(p.key)} sx={{ cursor: 'pointer' }}>
                  <TableCell sx={{ pl: 2.5, fontWeight: 600, maxWidth: 380 }}>{p.rule}</TableCell>
                  <TableCell><Chip size="small" label={p.severity.toUpperCase()} sx={{ fontWeight: 700, fontSize: 11, bgcolor: alpha(SEV_COLOR[p.severity] || SEV_COLOR.info, 0.16), color: SEV_COLOR[p.severity] || SEV_COLOR.info }} /></TableCell>
                  <TableCell sx={{ fontFamily: MONO, fontSize: 12.5, color: 'text.secondary', maxWidth: 260, wordBreak: 'break-all' }}>{p.resource || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{p.account}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{ago(p.when)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {shown.length > PAGE_SIZE && <Stack alignItems="center" sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}><Pagination count={Math.ceil(shown.length / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} size="small" /></Stack>}
      </Box>

      <Drawer anchor="right" open={!!open} onClose={() => setOpenKey(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 640 }, p: 3 } }}>
        {open && open.risk && (() => {
          const cats = byCategory(open.findings);
          const maxCat = Math.max(1, ...cats.map((c) => c.n));
          const t = trend(open);
          return (
            <Stack spacing={2.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{open.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{open.meta} · audited {ago(open.done.finished_at || open.done.created_at)}{took(open.done.started_at, open.done.finished_at) ? ` · took ${took(open.done.started_at, open.done.finished_at)}` : ''}</Typography>
                </Box>
                <IconButton aria-label="Close" onClick={() => setOpenKey(null)}><CloseIcon /></IconButton>
              </Stack>
              <Stack direction="row" gap={1}>
                <Button variant="contained" disabled={busy || !!open.live} onClick={() => run(() => repeat(open), 'Audit started.')} sx={{ bgcolor: GREEN, color: '#06220F', fontWeight: 700, '&:hover': { bgcolor: '#16a34a' } }}>Audit again</Button>
                <Button variant="outlined" onClick={() => navigate(`/cloud-scans?account=${encodeURIComponent(open.key)}`)} sx={{ borderColor: 'divider', color: 'text.primary', fontWeight: 600 }}>Reports and runs</Button>
              </Stack>
              <Stack direction="row" gap={2.5} alignItems="center" sx={{ ...card, p: 2.5 }}>
                <Ring score={open.risk.score} grade={open.risk.grade} size={96} />
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>Posture score {open.risk.score}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 17 }}>{GRADE_TEXT[open.risk.grade]}</Typography>
                  {t !== null && t !== 0 && <Typography variant="body2" sx={{ color: t > 0 ? '#4ade80' : '#fca5a5', fontWeight: 600 }}>{t > 0 ? 'Up' : 'Down'} {Math.abs(t)} since the last audit</Typography>}
                </Box>
              </Stack>
              {cats.length > 0 && (
                <Box sx={{ ...card, p: 2.5 }}>
                  <Typography sx={{ fontWeight: 700, mb: 1.5 }}>By kind of check</Typography>
                  {cats.slice(0, 8).map((c) => (
                    <Stack key={c.label} direction="row" alignItems="center" gap={1.5} sx={{ mb: 1.2 }}>
                      <Typography sx={{ width: 120, fontSize: 14, textTransform: 'capitalize' }} noWrap>{c.label}</Typography>
                      <Box sx={{ flex: 1, height: 10, borderRadius: 5, bgcolor: 'action.hover' }}><Box sx={{ height: '100%', width: `${(c.n / maxCat) * 100}%`, borderRadius: 5, bgcolor: '#f87171' }} /></Box>
                      <Typography sx={{ fontFamily: MONO, fontWeight: 600, width: 34, textAlign: 'right' }}>{c.n}</Typography>
                    </Stack>
                  ))}
                </Box>
              )}
              <Typography sx={{ fontWeight: 700 }}>Problems to fix ({open.findings.length})</Typography>
              {open.findings.length === 0 && <Typography variant="body2" color="text.secondary">The last audit found no problems.</Typography>}
              {[...open.findings].sort((x, y) => SEV.indexOf(x.severity) - SEV.indexOf(y.severity)).slice(0, 100).map((f) => (
                <Stack key={f.id} direction="row" alignItems="flex-start" gap={1.5} sx={{ pb: 1.6, borderBottom: 1, borderColor: 'divider' }}>
                  <Chip size="small" label={f.severity} sx={{ minWidth: 70, textTransform: 'capitalize', fontWeight: 700, bgcolor: alpha(SEV_COLOR[f.severity] || SEV_COLOR.info, 0.16), color: SEV_COLOR[f.severity] || SEV_COLOR.info }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600 }}>{f.rule}</Typography>
                    {f.resource && <Typography sx={{ fontFamily: MONO, fontSize: 12, color: 'text.secondary', wordBreak: 'break-all' }}>{f.resource}{f.region ? ` · ${f.region}` : ''}</Typography>}
                    {f.fix && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6 }}>How to fix: {f.fix}</Typography>}
                  </Box>
                </Stack>
              ))}
            </Stack>
          );
        })()}
      </Drawer>

      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)}>
        <DialogTitle>Stop this audit?</DialogTitle>
        <DialogContent><DialogContentText>The audit stops and results found so far are kept.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={busy} onClick={async () => { await run(() => cancelScan(confirm.id), 'Audit stopped.'); setConfirm(null); }}>Stop</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((p) => ({ ...p, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
